import React, { useState, useEffect } from "react";
import { Alert, AlertTitle, Typography } from "@mui/material";
import { restFetch } from "../../../../api/apiClient";
import { useRootStore } from "../../../../store/contexts";

/**
 * Banner de primer uso en el ALTA de editorial: si el tenant todavía no tiene
 * editorial propia asignada, avisa notoriamente que ESTA editorial quedará
 * establecida como la propia (se auto-asigna en el server, publisher/afterCreate).
 * Con editorial propia ya asignada, no se muestra (las siguientes altas van sin
 * ruido). Componente autónomo — el form generado solo lo importa y renderiza.
 */

const TEXT: Record<string, { title: string; body: string }> = {
  es: {
    title: "Esta será tu editorial propia",
    body: "Al crearla, quedará vinculada automáticamente a tu perfil (“Datos del Cliente”): es la editorial que firma tu documentación (DJCT) y participa en todas tus obras. Cargá sus datos reales y al menos un representante firmante con email. Por el momento la editorial propia no puede cambiarse.",
  },
  en: {
    title: "This will be your own publisher",
    body: "When you create it, it will be linked automatically to your profile (“Client Data”): it signs your documentation (DJCT) and participates in all your works. Enter its real data and at least one signing representative with email. For now your own publisher cannot be changed.",
  },
  pt: {
    title: "Esta será sua editora própria",
    body: "Ao criá-la, será vinculada automaticamente ao seu perfil (“Dados do Cliente”): é a editora que assina sua documentação (DJCT) e participa de todas as suas obras. Insira seus dados reais e ao menos um representante signatário com email. Por enquanto a editora própria não pode ser alterada.",
  },
};

export const FirstPublisherBanner: React.FC = () => {
  const { langStore } = useRootStore();
  const t = TEXT[langStore.language] ?? TEXT.es;
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await restFetch("/graphql", {
          method: "POST",
          body: JSON.stringify({
            query: `query TenantOwnPublisherExists {
              tenantProfileListWithOffset(offset: 0, take: 1) {
                ownPublisher { id }
              }
            }`,
          }),
        });
        const profile = data?.data?.tenantProfileListWithOffset?.[0];
        if (alive) setShow(!profile?.ownPublisher?.id);
      } catch {
        /* ante error, no molestar con el banner */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!show) return null;

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <AlertTitle>{t.title}</AlertTitle>
      <Typography variant="body2">{t.body}</Typography>
    </Alert>
  );
};
